import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Image as ImageIcon, Loader2, LockKeyhole, X } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useNoIndex } from "../lib/useNoIndex";
import { EmSwash } from "../components/EmSwash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const absolute = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const Pill = ({ on, onClick, testid, children }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-full px-3 py-1 text-[13px] font-semibold transition ${on ? "bg-ink text-sand" : "bg-bone text-ink ring-1 ring-inset ring-border hover:ring-ink"}`}>
    {children}
  </button>
);

const Field = ({ label, hint, children }) => (
  <label className="mt-5 block">
    <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[13px] text-olive/80">{hint}</span>}
  </label>
);

const inputCls = "mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 text-base text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";

export default function FoundingEdit() {
  useNoIndex();
  const navigate = useNavigate();
  const { token } = useParams();
  const fileRef = useRef(null);
  const [state, setState] = useState("checking"); // checking | invalid | expired | ready | saved
  const [detail, setDetail] = useState("");
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    axios.get(`${API}/founding-donors/edit/${token}`)
      .then((r) => {
        const d = r.data;
        setForm({ full_name: d.full_name || "", alma_mater: d.alma_mater || "", tribute: d.tribute || "",
          photo_url: d.photo_url || "", photo_filter: d.photo_filter || "color", anonymous: !!d.anonymous, expires_at: d.expires_at });
        setState("ready");
      })
      .catch((e) => {
        setDetail(e?.response?.data?.detail || "");
        setState(e?.response?.status === 410 ? "expired" : "invalid");
      });
  }, [token]);

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be under 5 MB."); return; }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/founding-donors/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ photo_url: data.url });
    } catch (err) {
      setError(err?.response?.data?.detail || "Photo upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim()) { setError("Please keep a name on your entry. Use the anonymous switch to hide it."); return; }
    setSaving(true);
    try {
      const { expires_at, ...body } = form;
      await axios.patch(`${API}/founding-donors/edit/${token}`, body);
      toast.success("Your entry is updated. It's live on the wall.");
      setState("saved");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? d[0]?.msg : (d || "Couldn't save your changes. Please try again."));
      if (err?.response?.status === 410) setState("expired");
      if (err?.response?.status === 404) setState("invalid");
    } finally {
      setSaving(false);
    }
  };

  const expiresLabel = form?.expires_at ? new Date(form.expires_at).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "";

  return (
    <main data-testid="founding-edit-page" className="bg-sand">
      <Navbar onStart={() => navigate("/founding")} onDonate={() => navigate("/")} primaryLabel="See the wall" secondaryLabel="Home" />
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 pt-32 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:pt-44">
        <div>
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">Founding donors</span>
          <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
            Your entry <EmSwash>on the wall.</EmSwash>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-olive">
            Fix a typo, add the school that shaped you, a few words about why you gave, or a photo from your student days. Changes appear on the wall the moment you save.
          </p>
          {state === "ready" && expiresLabel && (
            <p className="mt-4 text-[13px] text-olive" data-testid="founding-edit-expires">This private link works until {expiresLabel}.</p>
          )}
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-2xl border-2 border-tangerine bg-tangerine/[0.04] p-6 lg:p-8">
          {state === "checking" && (
            <p className="flex items-center gap-2 text-[13px] text-olive" data-testid="founding-edit-checking"><Loader2 className="h-4 w-4 animate-spin" /> Checking your link…</p>
          )}

          {(state === "invalid" || state === "expired") && (
            <div data-testid="founding-edit-invalid">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-scarlet/10 text-scarlet"><LockKeyhole className="h-6 w-6" strokeWidth={2} /></span>
              <h2 className="mt-4 font-serif text-3xl text-ink">{state === "expired" ? "This link has expired." : "This link isn't valid."}</h2>
              <p className="mt-3 text-base leading-relaxed text-olive">
                {detail || "Edit links are private and last 30 days."} Nothing is lost: your name is still on the wall, and we're glad to send a fresh link.
              </p>
              <a href="mailto:hellofrom@myrichaunt.org?subject=Please%20send%20a%20fresh%20edit%20link" data-testid="founding-edit-request-new"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand hover:bg-forest">
                Email us for a new link <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}

          {state === "saved" && (
            <div data-testid="founding-edit-saved">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-grass/15 text-grass"><Check className="h-6 w-6" strokeWidth={2.4} /></span>
              <h2 className="mt-4 font-serif text-3xl text-ink">Your entry is live.</h2>
              <p className="mt-3 text-base leading-relaxed text-olive">Thank you. The wall already shows your changes. You can come back and edit again any time while this link is active.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/founding" data-testid="founding-edit-see-wall" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand hover:bg-forest">
                  See the wall <ArrowRight className="h-4 w-4" />
                </Link>
                <button type="button" onClick={() => setState("ready")} data-testid="founding-edit-again"
                  className="inline-flex items-center rounded-full border border-ink/20 bg-bone px-5 py-3 text-base font-semibold text-ink hover:border-ink">
                  Keep editing
                </button>
              </div>
            </div>
          )}

          {state === "ready" && form && (
            <form onSubmit={save} data-testid="founding-edit-form">
              <h2 className="font-serif text-3xl text-ink">What the wall shows.</h2>

              <Field label="Name on the wall">
                <input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} maxLength={140} required
                  data-testid="founding-edit-name" className={inputCls} />
              </Field>

              <Field label="Alma mater (optional)">
                <input value={form.alma_mater} onChange={(e) => set({ alma_mater: e.target.value })} maxLength={200} placeholder="Spelman College"
                  data-testid="founding-edit-alma-mater" className={inputCls} />
              </Field>

              <Field label="Tribute (optional)" hint={`${form.tribute.length}/280`}>
                <textarea value={form.tribute} onChange={(e) => set({ tribute: e.target.value })} maxLength={280} rows={3}
                  placeholder="A few words about why you gave, or who you gave for." data-testid="founding-edit-tribute" className={`${inputCls} leading-relaxed`} />
              </Field>

              <div className="mt-5">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Photo (optional)</span>
                <div className="mt-2 rounded-md border border-dashed border-tangerine/40 bg-tangerine/[0.04] p-4">
                  {form.photo_url ? (
                    <div className="flex items-start gap-4">
                      <div className="relative shrink-0">
                        <img src={absolute(form.photo_url)} alt="Your wall photo" data-testid="founding-edit-photo-preview"
                          className={`h-24 w-24 rounded-md object-cover ${form.photo_filter === "bw" ? "grayscale" : ""}`} />
                        <button type="button" onClick={() => set({ photo_url: "" })} aria-label="Remove photo" data-testid="founding-edit-photo-remove"
                          className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-sand shadow">
                          <X className="h-3 w-3" strokeWidth={2.4} />
                        </button>
                      </div>
                      <div className="space-y-2">
                        <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">Photo tone</p>
                        <div className="flex gap-2">
                          <Pill testid="founding-edit-filter-color" on={form.photo_filter === "color"} onClick={() => set({ photo_filter: "color" })}>Color</Pill>
                          <Pill testid="founding-edit-filter-bw" on={form.photo_filter === "bw"} onClick={() => set({ photo_filter: "bw" })}>Black &amp; white</Pill>
                        </div>
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="founding-edit-photo-replace" className="text-[13px] font-semibold text-ink underline underline-offset-2 hover:text-tangerine">
                          Choose a different photo
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="founding-edit-photo-choose"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition hover:bg-forest disabled:opacity-60">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" strokeWidth={2} />}
                      {uploading ? "Uploading…" : "Choose a photo"}
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="founding-edit-photo-input" />
                  <p className="mt-2 text-[13px] text-olive/80">A favorite photo from college or grad school. JPG, PNG or WebP, under 5 MB.</p>
                </div>
              </div>

              <label className="mt-5 flex cursor-pointer items-center justify-between gap-4 rounded-md border border-border bg-bone p-4">
                <span>
                  <span className="block text-[13px] font-semibold text-ink">Show me as Anonymous</span>
                  <span className="block text-[13px] text-olive">Your name stays private; the wall lists you as "Anonymous."</span>
                </span>
                <button type="button" role="switch" aria-checked={form.anonymous} onClick={() => set({ anonymous: !form.anonymous })} data-testid="founding-edit-anonymous"
                  className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.anonymous ? "bg-tangerine" : "bg-ink/25"}`}>
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-sand shadow transition-transform ${form.anonymous ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </label>

              {error && <p role="alert" data-testid="founding-edit-error" className="mt-4 text-[13px] text-scarlet">{error}</p>}

              <button type="submit" disabled={saving || uploading} data-testid="founding-edit-save"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <>Save to the wall <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </section>
      <Footer onStart={() => navigate("/founding")} onDonate={() => navigate("/")} primaryLabel="See the wall" secondaryLabel="Home" />
    </main>
  );
}
